import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import { Trophy } from 'lucide-react';

const COUNTRY_CODES = [
  { code: '+62', flag: '\u{1F1EE}\u{1F1E9}', label: 'ID' },
  { code: '+1', flag: '\u{1F1FA}\u{1F1F8}', label: 'US' },
  { code: '+44', flag: '\u{1F1EC}\u{1F1E7}', label: 'GB' },
  { code: '+91', flag: '\u{1F1EE}\u{1F1F3}', label: 'IN' },
  { code: '+61', flag: '\u{1F1E6}\u{1F1FA}', label: 'AU' },
  { code: '+81', flag: '\u{1F1EF}\u{1F1F5}', label: 'JP' },
  { code: '+82', flag: '\u{1F1F0}\u{1F1F7}', label: 'KR' },
  { code: '+65', flag: '\u{1F1F8}\u{1F1EC}', label: 'SG' },
  { code: '+60', flag: '\u{1F1F2}\u{1F1FE}', label: 'MY' },
];

const FloatingInput = ({ id, label, type = 'text', value, onChange, autoComplete, error }) => (
  <div className="relative group">
    <input
      id={id}
      type={type}
      value={value}
      onChange={onChange}
      placeholder={label}
      autoComplete={autoComplete}
      required
      className={`block w-full bg-transparent border-0 border-b ${
        error ? 'border-red-400' : 'border-[#E8DCCA]'
      } py-4 px-0 text-[#2C2C2C] placeholder-transparent peer focus:outline-none focus:ring-0 focus:border-[#B8860B] transition-colors sm:text-sm`}
      style={{ fontFamily: "'Lato', sans-serif", borderRadius: 0 }}
    />
    <label
      htmlFor={id}
      className="absolute left-0 -top-3.5 text-[#555] text-xs transition-all peer-placeholder-shown:text-base peer-placeholder-shown:text-slate-400 peer-placeholder-shown:top-3 peer-focus:-top-3.5 peer-focus:text-[#B8860B] peer-focus:text-xs"
    >
      {label}
    </label>
    {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
  </div>
);

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register, isAuthenticated, loading } = useAuth();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [countryCode, setCountryCode] = useState('+62');
  const [whatsapp, setWhatsapp] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, loading, navigate]);

  const validate = () => {
    const errors = {};
    if (!name.trim()) errors.name = 'Name is required';
    if (!email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = 'Please enter a valid email';
    }
    if (!password) {
      errors.password = 'Password is required';
    } else if (password.length < 8) {
      errors.password = 'At least 8 characters';
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!validate()) return;

    setSubmitting(true);
    try {
      const phone = whatsapp.trim() ? `${countryCode}${whatsapp.trim()}` : '';
      await register(email, password, name.trim(), phone || undefined);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#B8860B] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Create Account — Our Day</title>
        <link
          href="https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;1,400&family=Lato:wght@300;400;700&display=swap"
          rel="stylesheet"
        />
      </Helmet>

      <div className="min-h-screen flex flex-col md:flex-row bg-[#FDFCF8]" style={{ fontFamily: "'Lato', sans-serif" }}>
        {/* Left: Hero Image (hidden on mobile) */}
        <div className="hidden md:block w-1/2 min-h-screen relative overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: "url('https://images.unsplash.com/photo-1519741497674-611481863552?w=1200&q=80')",
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
          <div className="absolute bottom-12 left-12 text-white z-10">
            <h2 className="text-4xl italic mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>Begin your forever.</h2>
            <p className="font-light tracking-wide text-white/90">Craft the perfect introduction to your love story.</p>
          </div>
        </div>

        {/* Right: Form */}
        <div className="w-full md:w-1/2 min-h-screen bg-[#FDFCF8] flex flex-col justify-center items-center p-8 md:p-16 lg:p-24 overflow-y-auto relative">
          {/* Decorative orbs */}
          <div className="absolute top-0 right-0 w-[300px] h-[300px] bg-[#E8DCCA]/20 rounded-full blur-[100px] pointer-events-none opacity-50" />
          <div className="absolute bottom-0 left-0 w-[200px] h-[200px] bg-[#B8860B]/10 rounded-full blur-[80px] pointer-events-none opacity-40" />

          <div className="w-full max-w-md z-10">
            {/* Logo */}
            <div className="flex flex-col items-center mb-10">
              <Trophy className="w-12 h-12 text-[#B8860B] mb-2" />
              <span
                className="text-3xl font-bold tracking-wide"
                style={{
                  fontFamily: "'Playfair Display', serif",
                  background: 'linear-gradient(to right, #B8860B, #D4AF37, #B8860B)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Our Day
              </span>
              <div className="h-px w-16 bg-gradient-to-r from-transparent via-[#B8860B] to-transparent mt-4 opacity-50" />
            </div>

            {/* Heading */}
            <div className="text-center mb-10">
              <h1 className="text-4xl text-[#2C2C2C] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>Create Your Account</h1>
              <p className="text-[#555] font-light text-sm">Join thousands of couples designing their dream invitations.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm text-sm">
                  {error}
                </div>
              )}

              <FloatingInput
                id="fullname"
                label="Full Name"
                value={name}
                onChange={(e) => { setName(e.target.value); if (fieldErrors.name) setFieldErrors((p) => ({ ...p, name: '' })); }}
                autoComplete="name"
                error={fieldErrors.name}
              />

              <FloatingInput
                id="email"
                label="Email Address"
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); if (fieldErrors.email) setFieldErrors((p) => ({ ...p, email: '' })); }}
                autoComplete="email"
                error={fieldErrors.email}
              />

              {/* Phone: Country Code + WhatsApp Number */}
              <div className="flex gap-4 items-end">
                <div className="w-1/3 relative">
                  <label htmlFor="country" className="sr-only">Country Code</label>
                  <select
                    id="country"
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                    className="block w-full bg-transparent border-0 border-b border-[#E8DCCA] py-4 px-0 text-[#555] focus:text-[#2C2C2C] focus:outline-none focus:ring-0 focus:border-[#B8860B] cursor-pointer text-sm transition-colors"
                    style={{ borderRadius: 0 }}
                  >
                    {COUNTRY_CODES.map((c) => (
                      <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
                    ))}
                  </select>
                </div>
                <div className="w-2/3 relative group">
                  <input
                    id="whatsapp"
                    type="tel"
                    value={whatsapp}
                    onChange={(e) => setWhatsapp(e.target.value)}
                    placeholder="WhatsApp Number"
                    className="block w-full bg-transparent border-0 border-b border-[#E8DCCA] py-4 px-0 text-[#2C2C2C] placeholder-transparent peer focus:outline-none focus:ring-0 focus:border-[#B8860B] transition-colors sm:text-sm"
                    style={{ borderRadius: 0 }}
                  />
                  <label
                    htmlFor="whatsapp"
                    className="absolute left-0 -top-3.5 text-[#555] text-xs transition-all peer-placeholder-shown:text-base peer-placeholder-shown:text-slate-400 peer-placeholder-shown:top-3 peer-focus:-top-3.5 peer-focus:text-[#B8860B] peer-focus:text-xs"
                  >
                    WhatsApp Number
                  </label>
                </div>
              </div>

              <FloatingInput
                id="password"
                label="Password"
                type="password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); if (fieldErrors.password) setFieldErrors((p) => ({ ...p, password: '' })); }}
                autoComplete="new-password"
                error={fieldErrors.password}
              />

              <div className="pt-6">
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#E8DCCA] hover:bg-[#D1C0A8] text-[#2C2C2C] py-4 rounded-sm text-base font-semibold transition-all border border-[#D1C0A8] shadow-sm hover:shadow-md tracking-widest uppercase disabled:opacity-50 flex items-center justify-center"
                  style={{ fontFamily: "'Playfair Display', serif", letterSpacing: '0.05em' }}
                >
                  {submitting ? (
                    <div className="w-5 h-5 border-2 border-[#2C2C2C] border-t-transparent rounded-full animate-spin" />
                  ) : (
                    'Create Your Story'
                  )}
                </button>
              </div>
            </form>

            {/* Login link */}
            <div className="mt-8 text-center">
              <p className="text-sm text-[#555] font-light">
                Already have an account?{' '}
                <Link
                  to="/login"
                  className="text-[#B8860B] hover:text-[#D4AF37] border-b border-[#B8860B]/30 hover:border-[#B8860B] transition-all pb-0.5 ml-1 italic font-medium"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Login here
                </Link>
              </p>
            </div>

            {/* Footer links */}
            <div className="mt-12 flex justify-center gap-6 opacity-60">
              <span className="text-xs text-slate-400 tracking-widest uppercase font-light">Privacy Policy</span>
              <span className="text-xs text-slate-400 tracking-widest uppercase font-light">Terms of Service</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default RegisterPage;
